import { Box, Button, Stack, Typography, useColorScheme } from "@mui/material";
import { random } from "lodash-es";
import moment from "moment-timezone";
import React from "react";
import { useTranslation } from "react-i18next";
import { useI18nLanguage } from "../../hooks/i18nLanguage";
import { Participant } from "../../services/api/types/availability.type";
import {
  focusRingStyles,
  formatTimeForScreenReader,
  generateA11yId
} from "../../utils/accessibility";
import { extractDateFromISODateTime } from "../../utils/dateTime";
import { ColumnViewSkeleton } from "../Skeleton/ColumnViewSkeleton";

interface Slot {
  hour: number;
  minute: number;
  participants: Participant[];
}

interface AvailabilityDay {
  date: string;
  availability: Slot[];
}

interface AvailableSlotsGridProps {
  computedAvailability: AvailabilityDay[];
  selectedSlotDate: number;
  clientId: string | null;
  loadingAvailability: boolean;
  onSlotClick: (slot: {
    hour: number;
    minute: number;
    date: number;
    participants: Participant[];
  }) => void;
}

export const AvailableSlotsGrid: React.FC<AvailableSlotsGridProps> = ({
  computedAvailability,
  selectedSlotDate,
  clientId,
  loadingAvailability,
  onSlotClick
}) => {
  const { i18n } = useTranslation();
  const { mode: themeVariant } = useColorScheme();
  const { selectedLanguageForMoment } = useI18nLanguage();

  if (!clientId || loadingAvailability || !computedAvailability?.length) {
    return (
      <Box
        role="status"
        aria-busy="true"
        aria-label="Loading available time slots"
      >
        <ColumnViewSkeleton columns={5} minSlots={6} maxSlots={random(6, 12)} />
      </Box>
    );
  }

  return (
    <Box
      role="group"
      aria-label="Available time slots for scheduling"
      sx={{ display: "contents" }}
    >
      {computedAvailability
        ?.filter((d) => moment(d.date).isSame(selectedSlotDate, "week"))
        .map((i) => {
          const date = moment(extractDateFromISODateTime(i.date));
          return (
            <Stack
              key={i.date}
              direction="column"
              alignItems="center"
              flex={1}
              role="group"
              aria-labelledby={generateA11yId("date-header", i.date)}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography
                  component="h2"
                  id={generateA11yId("date-header", i.date)}
                  sx={{ fontSize: "14px", fontWeight: 450 }}
                >
                  {date
                    .clone()
                    .locale(selectedLanguageForMoment)
                    .format("ddd , ")}
                  {date.format("DD")}
                </Typography>
              </Stack>

              <Stack
                direction="column"
                sx={{ paddingTop: 2 }}
                spacing={"12px"}
                width="100%"
              >
                {i.availability.map((slot) => (
                  <Button
                    key={slot.hour + slot.minute}
                    aria-label={formatTimeForScreenReader(
                      slot.hour,
                      slot.minute,
                      date.toDate(),
                      i18n.language
                    )}
                    sx={{
                      // Ensure 44x44px minimum touch target (WCAG 2.5.5)
                      minHeight: "44px",
                      minWidth: "44px",
                      ...(themeVariant === "light"
                        ? {
                            borderColor: "#375990",
                            color: "#375990"
                          }
                        : {
                            borderColor: "#6B6B6B", // ~3.3:1 contrast on #1f1f1f (meets WCAG 2.1 AA, SC 1.4.11)
                            color: "#9c9c9c",
                            backgroundColor: "#1f1f1f",
                            ":hover": {
                              backgroundColor: "#282828"
                            }
                          }),
                      ...focusRingStyles
                    }}
                    variant="outlined"
                    onClick={() => {
                      onSlotClick({
                        date: date.startOf("day").valueOf(),
                        hour: slot.hour,
                        minute: slot.minute,
                        participants: slot.participants
                      });
                    }}
                  >
                    {moment()
                      .startOf("day")
                      .set("hour", slot.hour)
                      .set("minutes", slot.minute)
                      .locale(selectedLanguageForMoment)
                      .format("LT")
                      .toLowerCase()}
                  </Button>
                ))}
              </Stack>
            </Stack>
          );
        })}
    </Box>
  );
};
